import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import authService from '../services/authService';
import { showToast } from '../utils/toastUtils';

interface AuthContextType {
  isAuthenticated: boolean;
  user: any;
  loading: boolean;
  login: (identifier: string, otp: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = useCallback(() => {
    try {
      const authenticated = authService.isAuthenticated();
      const currentUser = authService.getCurrentUser();

      console.log('🔐 Checking auth status:', { authenticated, currentUser });

      // Only check if we have tokens, don't check refresh token expiry
      if (authenticated && currentUser) {
        console.log('🔐 User has tokens and is authenticated');
      }

      setIsAuthenticated(authenticated);
      setUser(currentUser);

      console.log('🔐 Automatic token refresh disabled - will only refresh on 401 responses');
    } catch (error) {
      console.error('Error checking auth status:', error);
      // Don't logout on general error, just set as not authenticated
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check authentication status on mount only
    checkAuthStatus();

    // Listen for logout events from axios interceptor
    const handleLogoutEvent = () => {
      console.log('🔐 Received logout event from axios interceptor');
      showToast.warning('Your session has expired. Please log in again.');
      handleLogout();
      // Navigate to login page using window.location
      window.location.href = '/login';
    };

    window.addEventListener('auth:logout', handleLogoutEvent);

    // No more periodic token validation - only refresh on 401 responses
    console.log('🔐 Periodic token validation disabled - will only refresh on 401 responses');

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
  }, [checkAuthStatus]);

  const login = async (identifier: string, otp: string): Promise<boolean> => {
    try {
      console.log('🔐 AuthContext: Starting login process');
      setLoading(true);
      const response = await authService.verifyOTP(identifier, otp);
      console.log('🔐 AuthContext: OTP verification response:', response);

      if (response.success) {
        console.log('🔐 AuthContext: Login successful, updating state');
        setIsAuthenticated(true);
        setUser(response.user);
        console.log('🔐 AuthContext: State updated, returning true');
        return true;
      } else {
        console.log('🔐 AuthContext: Login failed, returning false');
        return false;
      }
    } catch (error) {
      console.error('🔐 AuthContext: Login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };


  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  };

  const logout = () => {
    handleLogout();
    // Navigate to login page using window.location
    window.location.href = '/login';
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
